// The ONE home of the five public quasi-static core-data collections
// (ui-conventions.md #94, owner ruling 2026-08-22): tenants, competitions,
// events, teams, universities. Stale-while-revalidate — render whatever is
// in localStorage instantly, revalidate in the background, invalidate via
// the live store the moment a write lands (the module's one invalidation
// path — src/core/README.md "Invalidation").
//
// Persons is NOT one of the five: it's a server-search-driven Relay
// connection over a population too large to cache wholesale (ui-conventions
// #13/#14), so it stays outside this store entirely.
//
// Trigger: App.vue calls ensureLoaded() from a post-paint idle callback, not
// from this store's own init — a store that fetched on construction would
// race component mounts for the network and delay first paint (#27 wants
// warm caches on startup, not blocking startup).
import { defineStore } from 'pinia'
import { computed, inject, ref, watch, type Ref } from 'vue'
import { cancellableQuery } from 'src/lib/cancellableQuery'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import type { Client } from '@urql/vue'
import { useLiveStore } from 'src/stores/live'
import { useAuthStore } from 'src/stores/auth'
import {
  CompetitionsDocument,
  EventsDocument,
  TeamsDocument,
  TenantsDocument,
  UniversitiesDocument,
} from 'src/modules/core/core-data.operations'
import type {
  CompetitionsQuery,
  EventsQuery,
  TeamsQuery,
  TenantsQuery,
  UniversitiesQuery,
} from 'src/graphql/graphql'

const STORAGE_PREFIX = 'portal.coreData.v1.'

export type CoreDataTenant = TenantsQuery['tenants'][number]
export type CoreDataCompetition = CompetitionsQuery['competitions'][number]
export type CoreDataEvent = EventsQuery['events'][number]
export type CoreDataUniversity = UniversitiesQuery['universities'][number]

/** The team row as the WIRE carries it: `university` is the bare reference
 *  (fleet-fix #118 — the seven university fields the tables read were a
 *  verbatim per-team copy of the universities collection cached right here,
 *  ~30% of the portal's largest payload). Nothing outside this store sees
 *  this shape. */
type WireTeam = TeamsQuery['teams'][number]

/** The university fields a team row PRESENTS — joined out of the cached
 *  `universities` collection, never shipped with the team. Every consumer
 *  field (TeamsPage's university/country facets + haystack, VehiclesPage's
 *  country facet + haystack, UniversitiesPage's team-by-university filter,
 *  ImpersonationDialog's search, src/core/teamDisplay.ts's "CC - City Label")
 *  is in here. */
export type CoreDataTeamUniversity = Pick<
  CoreDataUniversity,
  'id' | 'name' | 'nameShort' | 'nameInternal' | 'nameLocal' | 'city' | 'country'
>

/** What every consumer reads: the wire row with its university joined back
 *  in. Identical to the pre-#118 shape, so no consumer changed. */
export type CoreDataTeam = Omit<WireTeam, 'university'> & {
  university: CoreDataTeamUniversity
}

type CollectionKey = 'tenants' | 'competitions' | 'events' | 'teams' | 'universities'

/** Which live-store kinds (src/stores/live.ts) invalidate each collection —
 *  one table, so "what does teams watch" has exactly one place to look
 *  (#24, #68) rather than one watcher hand-written per collection. */
const LIVE_KINDS: Record<CollectionKey, string[]> = {
  tenants: ['tenants'],
  competitions: ['competitions', 'tenants'],
  events: ['events', 'event_classes', 'competitions', 'teams'],
  // 'participations' added alongside core-admin-crud's updateParticipation
  // (specs/core-admin-crud/SPEC.md): the cached Teams list embeds each
  // vehicle's participations (status, event ref) for TeamsPage's Event/
  // Status facet — an admin override on status must refresh it here too,
  // not just the sidebar's on-demand DETAIL query.
  //
  // NOT 'team_billing'/'team_contact' (fleet-fix #118): the #94 LIST/DETAIL
  // split moved billing and contact out of the Teams LIST query entirely, so
  // a write to either changed nothing this collection carries — yet it
  // re-pulled the whole multi-megabyte Teams payload. Those two kinds are
  // the DETAIL query's business, and TeamDetailPanel already watches them
  // through its own refetchOnChange.
  //
  // NOT 'universities' either, since #118: the university fields now come
  // from the `universities` collection at read time (see teamsJoined below),
  // so a university write refreshes THAT collection and the join follows —
  // re-pulling the whole Teams payload for it would be the same waste.
  teams: ['teams', 'vehicles', 'participations'],
  universities: ['universities', 'teams'],
}

interface PersistedEntry<T> {
  fetchedAt: number
  data: T[]
}

interface CollectionState<T> {
  data: Ref<T[]>
  fetching: Ref<boolean>
  error: Ref<{ message: string } | null>
  fetchedAt: Ref<number | null>
  /** In-flight request handle so a superseding load (live bump during an
   *  active load) cancels rather than races it (owner ruling 2026-08-24).
   *  Plain mutable field, deliberately not a Ref — connection state, not
   *  application state. */
  inflight: { cancel(): void } | null
  /** True once THIS SESSION has successfully fetched the collection — the
   *  guard that makes ensureLoaded() a no-op on every navigation after the
   *  first (owner fix 2026-08-24: per-navigation full reloads are exactly
   *  what live invalidation exists to make unnecessary). A persisted copy
   *  from a PREVIOUS session does not count: SWR still revalidates once
   *  per pageload. Deliberately not persisted. */
  sessionFresh: Ref<boolean>
}

let quotaWarned = false

function loadPersisted<T>(key: CollectionKey): PersistedEntry<T> | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !Array.isArray((parsed as { data?: unknown }).data) ||
      typeof (parsed as { fetchedAt?: unknown }).fetchedAt !== 'number'
    ) {
      return null
    }
    return parsed as PersistedEntry<T>
  } catch {
    // Corrupt JSON, or localStorage unavailable (private browsing, etc.) —
    // empty is the only safe fallback; this store never throws on read.
    return null
  }
}

function savePersisted<T>(key: CollectionKey, entry: PersistedEntry<T>): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry))
  } catch (e) {
    // QuotaExceededError (or any other write failure) → memory-only for the
    // rest of the session. Warn once, not per collection per revalidate — a
    // console flooded on every background refetch is as bad as no warning.
    if (!quotaWarned) {
      quotaWarned = true
      console.warn('coreData: persisting to localStorage failed, continuing memory-only', e)
    }
  }
}

function makeCollection<T>(key: CollectionKey): CollectionState<T> {
  const persisted = loadPersisted<T>(key)
  return {
    inflight: null,
    sessionFresh: ref(false),
    data: ref(persisted?.data ?? []) as Ref<T[]>,
    fetching: ref(false),
    error: ref(null),
    fetchedAt: ref(persisted?.fetchedAt ?? null),
  }
}

// The provide/inject key @urql/vue's own `install()`/`useClientHandle()`
// default to (their own default parameter, not exported as a constant — see
// node_modules/@urql/vue's install/useClientHandle source). Reading the
// client this way, rather than through src/boot/urql.ts's own client
// management, is deliberate: that file imports Quasar's `#q-app` boot alias,
// which only resolves inside Quasar's own Vite pipeline — not under plain
// vitest (src/modules/core/test/testHarness.ts already works around the
// same class of problem for src/router/routes.ts). Injecting the SAME key
// every `useQuery` call already resolves through means this store picks up
// exactly the same client in production AND in every component test that
// installs its own urql plugin instance, with no special-casing needed.
const URQL_CLIENT_KEY = '$urql'

/** Fires one list query and populates its collection state + persistence.
 *  `network-only` when a persisted copy already renders (revalidate);
 *  `cache-first` on a genuinely cold load, so mock-mode/dev doesn't double
 *  the very first request. */
async function loadCollection<TQuery, TRow>(
  key: CollectionKey,
  document: TypedDocumentNode<TQuery, Record<string, never>>,
  pluck: (data: TQuery) => TRow[],
  state: CollectionState<TRow>,
  clientRef: Ref<Client> | undefined,
): Promise<void> {
  const client = clientRef?.value
  if (!client) {
    state.error.value = { message: 'no GraphQL client available' }
    return
  }
  state.inflight?.cancel()
  const hadPersisted = state.data.value.length > 0
  state.fetching.value = true
  const query = cancellableQuery(client, document, {} as Record<string, never>, {
    requestPolicy: hadPersisted ? 'network-only' : 'cache-first',
  })
  state.inflight = query
  try {
    const result = await query.result
    // Cancelled — the superseding load owns fetching/inflight now.
    if (!result) return
    if (result.error) {
      state.error.value = { message: result.error.message }
      return
    }
    if (!result.data) return
    const rows = pluck(result.data)
    state.data.value = rows
    state.error.value = null
    state.fetchedAt.value = Date.now()
    state.sessionFresh.value = true
    savePersisted(key, { fetchedAt: state.fetchedAt.value, data: rows })
  } catch (e) {
    state.error.value = { message: e instanceof Error ? e.message : 'network error' }
  } finally {
    if (state.inflight === query) {
      state.inflight = null
      state.fetching.value = false
    }
  }
}

/** Whether this session can fetch the five collections at all (ui-report
 *  01a03b1f): mock mode counts as signed in; otherwise the server must have
 *  answered `Query.me` with a LINKED person — the pre-auth funnel carries no
 *  bearer and an unlinked account is refused, so fetching earlier only
 *  produces Sentry noise. App.vue watches this to fire the idle prefetch
 *  once it turns true. */
export function coreDataSessionReady(auth: ReturnType<typeof useAuthStore>): boolean {
  return auth.mock || auth.serverViewer?.person != null
}

export const useCoreDataStore = defineStore('coreData', () => {
  const live = useLiveStore()
  for (const kinds of Object.values(LIVE_KINDS)) for (const kind of kinds) live.register(kind)

  // Injected once, synchronously, at store setup — inject() only resolves
  // within an active component/app context, so it cannot be called lazily
  // from inside the async loadCollection() below. The captured value is the
  // REF itself, not a client snapshot, so every load still reads whatever
  // client is CURRENT at call time (mind resetGraphqlClient swapping).
  const clientRef = inject<Ref<Client> | undefined>(URQL_CLIENT_KEY, undefined)

  const tenants = makeCollection<CoreDataTenant>('tenants')
  const competitions = makeCollection<CoreDataCompetition>('competitions')
  const events = makeCollection<CoreDataEvent>('events')
  const teams = makeCollection<WireTeam>('teams')
  const universities = makeCollection<CoreDataUniversity>('universities')

  // ── the Teams ↔ Universities join (fleet-fix #118) ─────────────────────
  // The Teams LIST query carries `university { id }` and nothing more; the
  // fields the tables render come from the `universities` collection this
  // same store already caches, joined by id at read time — the way the Event
  // facet already labels off the cached `events` rather than carrying event
  // fields through every team (#95).
  //
  // Order of arrival is not guaranteed: ensureLoaded fires all five in
  // parallel, and teams (the largest payload) can land before or after
  // universities. Until universities are in, `teams` is EMPTY and
  // teamsFetching stays true — a team rendered with an undefined university
  // is exactly the "renders empty, only correct after F5" bug (#29/#30).
  // "In" means a completed fetch, not a non-empty array, so a genuinely
  // empty collection resolves instead of spinning forever.
  const universitiesReady = computed(
    () => universities.fetchedAt.value !== null || universities.data.value.length > 0,
  )
  /** Team rows are in, their universities are not — the one state in which
   *  the Teams surfaces must show "loading" rather than "no data". */
  const teamsJoinPending = computed(() => teams.data.value.length > 0 && !universitiesReady.value)
  const teamsJoined = computed<CoreDataTeam[]>(() => {
    if (!universitiesReady.value) return []
    const byId = new Map(universities.data.value.map((u) => [u.id, u]))
    const rows: CoreDataTeam[] = []
    for (const t of teams.data.value) {
      const u = byId.get(t.university.id)
      // A team whose university is not in the collection cannot be rendered
      // (both queries pass includeHidden: true, so this only happens against
      // a stale persisted copy mid-revalidation). Skipping beats printing a
      // row with blank university, country and display name.
      if (!u) continue
      rows.push({
        ...t,
        university: {
          id: u.id,
          name: u.name,
          nameShort: u.nameShort,
          nameInternal: u.nameInternal,
          nameLocal: u.nameLocal,
          city: u.city,
          country: u.country,
        },
      })
    }
    return rows
  })

  const loadTenants = () =>
    loadCollection('tenants', TenantsDocument, (d) => d.tenants, tenants, clientRef)
  const loadCompetitions = () =>
    loadCollection(
      'competitions',
      CompetitionsDocument,
      (d) => d.competitions,
      competitions,
      clientRef,
    )
  const loadEvents = () =>
    loadCollection('events', EventsDocument, (d) => d.events, events, clientRef)
  const loadTeams = () => loadCollection('teams', TeamsDocument, (d) => d.teams, teams, clientRef)
  const loadUniversities = () =>
    loadCollection(
      'universities',
      UniversitiesDocument,
      (d) => d.universities,
      universities,
      clientRef,
    )

  /** Loads whatever this session has NOT successfully fetched yet — and
   *  nothing else. Called by every core page on mount, so after the first
   *  navigation this is a complete no-op (owner fix 2026-08-24: navigating
   *  used to fire all five list queries every time, which live invalidation
   *  makes entirely unnecessary). Freshness after that first fetch is owned
   *  by the invalidation watchers below (per-kind notices, plus the live
   *  store's refreshAll() bumping every kind on wake/reconnect) — never by
   *  navigation. A failed load leaves sessionFresh false, so the next
   *  ensureLoaded (navigation or a LoadingError retry) tries again. */
  function ensureLoaded(): void {
    // Session-aware (ui-report 01a03b1f): without a session-ready viewer the
    // five queries can only fail — the pre-auth funnel carries no bearer at
    // all, an unlinked account is refused on the non-public selections — and
    // every failure lands in Sentry as noise. App.vue watches readiness and
    // re-triggers the prefetch the moment it turns true, so a call refused
    // here is never lost.
    if (!coreDataSessionReady(useAuthStore())) return
    const pairs: Array<[CollectionState<unknown>, () => Promise<void>]> = [
      [tenants, loadTenants],
      [competitions, loadCompetitions],
      [events, loadEvents],
      [teams, loadTeams],
      [universities, loadUniversities],
    ]
    for (const [state, load] of pairs) {
      if (!state.sessionFresh.value && !state.fetching.value) void load()
    }
  }

  // Live invalidation (#24 — the one path, mirroring
  // src/composables/useLive.ts's own watcher): a kind bumping refetches
  // exactly the collections that named it in LIVE_KINDS, network-only, and
  // updates persistence once the refetch lands.
  function watchInvalidation(key: CollectionKey, reload: () => void): void {
    watch(
      () => LIVE_KINDS[key].map((k) => live.revision(k)).join(':'),
      (now, before) => {
        if (now === before) return
        reload()
      },
    )
  }
  watchInvalidation('tenants', loadTenants)
  watchInvalidation('competitions', loadCompetitions)
  watchInvalidation('events', loadEvents)
  watchInvalidation('teams', loadTeams)
  watchInvalidation('universities', loadUniversities)

  return {
    tenants: computed(() => tenants.data.value),
    tenantsFetching: computed(() => tenants.fetching.value),
    tenantsError: computed(() => tenants.error.value),
    tenantsFetchedAt: computed(() => tenants.fetchedAt.value),

    competitions: computed(() => competitions.data.value),
    competitionsFetching: computed(() => competitions.fetching.value),
    competitionsError: computed(() => competitions.error.value),
    competitionsFetchedAt: computed(() => competitions.fetchedAt.value),

    events: computed(() => events.data.value),
    eventsFetching: computed(() => events.fetching.value),
    eventsError: computed(() => events.error.value),
    eventsFetchedAt: computed(() => events.fetchedAt.value),

    teams: teamsJoined,
    // The join's dependencies are part of the collection's loading state:
    // teams answered but universities still outstanding is "still loading",
    // and a universities failure is what the Teams table must report and
    // retry on (ensureLoaded retries whichever collection is not fresh).
    teamsFetching: computed(
      () => teams.fetching.value || (teamsJoinPending.value && universities.error.value === null),
    ),
    teamsError: computed(
      () => teams.error.value ?? (teamsJoinPending.value ? universities.error.value : null),
    ),
    teamsFetchedAt: computed(() => teams.fetchedAt.value),

    universities: computed(() => universities.data.value),
    universitiesFetching: computed(() => universities.fetching.value),
    universitiesError: computed(() => universities.error.value),
    universitiesFetchedAt: computed(() => universities.fetchedAt.value),

    ensureLoaded,
  }
})
